import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Text } from "grommet";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import "./TagPickerButton.css";

const DEFAULT_TAGS = [
  "firstName",
  "lastName",
  "checkinDate",
  "checkinTime",
  "checkoutDate",
  "checkoutTime",
];

const TAG_LABELS = {
  chatLink: "Chat link",
  hotelPhone: "Hotel Phone",
  magicLink: "Guest app",
  firstName: "First Name",
  lastName: "Last Name",
  checkinDate: "Check-in Date",
  checkinTime: "Check-in Time",
  checkoutDate: "Check-out Date",
  checkoutTime: "Check-out Time",
  roomsAndCodes: "Rooms & Codes",
};

// Convert plain text (with {tag} tokens) to HTML for contentEditable
function textToHtml(text) {
  if (!text) return "";
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const withTags = escaped.replace(/\{(\w+)\}/g, (match, tag) => {
    const label = TAG_LABELS[tag] || tag;
    return (
      '<span class="tag-pill" contenteditable="false" data-tag="' +
      tag +
      '">' +
      label +
      "</span>"
    );
  });
  return withTags.replace(/\n/g, "<br>");
}

// Convert contentEditable HTML back to plain text with {tag} tokens
function getPlainText(el) {
  let text = "";
  const walk = (node, isFirst) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent.replace(/\u200B/g, "");
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.classList && node.classList.contains("tag-pill")) {
        text += "{" + node.getAttribute("data-tag") + "}";
      } else if (node.tagName === "BR") {
        text += "\n";
      } else {
        if (node.tagName === "DIV" && !isFirst) {
          text += "\n";
        }
        let first = true;
        for (const child of node.childNodes) {
          walk(child, first);
          first = false;
        }
      }
    }
  };
  let first = true;
  for (const child of el.childNodes) {
    walk(child, first);
    first = false;
  }
  return text;
}

export default function TagTextArea({
  value = "",
  onChange,
  onBlur,
  tags = DEFAULT_TAGS,
  style,
  ...rest
}) {
  const { t } = useTranslation();
  const savedSelection = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [filter, setFilter] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const editorRef = useRef(null);
  const menuRef = useRef(null);
  const braceNodeRef = useRef(null);
  const braceOffsetRef = useRef(-1);
  const isInternalChange = useRef(false);

  useEffect(() => {
    const rememberSelection = () => {
      const selection = window.getSelection();
      if (selection?.rangeCount && editorRef.current?.contains(selection.anchorNode)) {
        savedSelection.current = selection.getRangeAt(0).cloneRange();
      }
    };
    document.addEventListener("selectionchange", rememberSelection);
    return () => document.removeEventListener("selectionchange", rememberSelection);
  }, []);

  const filteredTags = tags.filter((t) =>
    t.toLowerCase().startsWith(filter.toLowerCase()),
  );

  // Sync external value changes into contentEditable
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    const el = editorRef.current;
    if (!el) return;
    const currentText = getPlainText(el);
    if (currentText !== value) {
      el.innerHTML = textToHtml(value);
    }
  }, [value]);

  const insertTag = useCallback(
    (tag) => {
      const el = editorRef.current;
      if (!el) return;

      const textNode = braceNodeRef.current;
      const braceOffset = braceOffsetRef.current;

      if (textNode && textNode.parentNode) {
        const fullText = textNode.textContent;
        const beforeBrace = fullText.slice(0, braceOffset);
        const afterPartial = fullText.slice(braceOffset + 1 + filter.length);

        const pill = document.createElement("span");
        pill.className = "tag-pill";
        pill.contentEditable = "false";
        pill.setAttribute("data-tag", tag);
        pill.textContent = TAG_LABELS[tag] || tag;

        const parent = textNode.parentNode;

        if (beforeBrace) {
          parent.insertBefore(document.createTextNode(beforeBrace), textNode);
        }
        parent.insertBefore(pill, textNode);

        const afterText = afterPartial || "\u200B";
        const afterNode = document.createTextNode(afterText);
        parent.insertBefore(afterNode, textNode);
        parent.removeChild(textNode);

        // Place cursor after the pill
        const sel = window.getSelection();
        const range = document.createRange();
        range.setStart(afterNode, afterPartial ? 0 : 1);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);

        isInternalChange.current = true;
        const newText = getPlainText(el);
        onChange(newText);
      }

      setShowMenu(false);
      setFilter("");
    },
    [filter, onChange],
  );

  const handleKeyDown = (e) => {
    if (!showMenu) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, filteredTags.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (filteredTags.length > 0) {
        e.preventDefault();
        insertTag(filteredTags[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowMenu(false);
      setFilter("");
    }
  };

  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;

    const sel = window.getSelection();
    if (!sel.rangeCount) {
      setShowMenu(false);
      isInternalChange.current = true;
      onChange(getPlainText(el));
      return;
    }

    const range = sel.getRangeAt(0);
    const textNode = range.startContainer;

    if (textNode.nodeType !== Node.TEXT_NODE) {
      setShowMenu(false);
      setFilter("");
      isInternalChange.current = true;
      onChange(getPlainText(el));
      return;
    }

    const cursorOffset = range.startOffset;
    const textBeforeCursor = textNode.textContent.slice(0, cursorOffset);
    const lastBrace = textBeforeCursor.lastIndexOf("{");
    const lastCloseBrace = textBeforeCursor.lastIndexOf("}");

    if (lastBrace > lastCloseBrace) {
      const partial = textBeforeCursor.slice(lastBrace + 1);
      if (/^[a-zA-Z]*$/.test(partial)) {
        braceNodeRef.current = textNode;
        braceOffsetRef.current = lastBrace;
        setFilter(partial);
        setSelectedIndex(0);
        setShowMenu(true);
        positionMenu();
        isInternalChange.current = true;
        const newText = getPlainText(el);
        onChange(newText);
        return;
      }
    }

    setShowMenu(false);
    setFilter("");
    isInternalChange.current = true;
    const newText = getPlainText(el);
    onChange(newText);
  };

  const positionMenu = () => {
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const editorRect = editorRef.current.parentElement.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom - editorRect.top + 4,
      left: Math.max(
        0,
        Math.min(rect.left - editorRect.left, editorRect.width - 180),
      ),
    });
  };

  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        e.target !== editorRef.current &&
        !editorRef.current?.contains(e.target)
      ) {
        setShowMenu(false);
        setFilter("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showMenu]);

  const openTagPicker = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection) return;
    let range = selection.rangeCount && editor.contains(selection.anchorNode)
      ? selection.getRangeAt(0).cloneRange() : savedSelection.current?.cloneRange();
    if (!range || !editor.contains(range.startContainer)) {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    range.collapse(true);
    editor.focus();
    const before = range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.textContent.slice(0, range.startOffset) : "";
    if (!before.endsWith("{")) {
      const brace = document.createTextNode("{");
      range.insertNode(brace);
      range.setStart(brace, 1);
      range.collapse(true);
    }
    selection.removeAllRanges();
    selection.addRange(range);
    savedSelection.current = range.cloneRange();
    handleInput();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  return (
    <Box style={{ position: "relative" }}>
      <style>
        {".tag-pill {" +
          "  display: inline-block;" +
          "  background: #e0f7fa;" +
          "  border: 1px solid #4dd0e1;" +
          "  border-radius: 12px;" +
          "  padding: 1px 8px;" +
          "  margin: 0 2px;" +
          "  font-size: 13px;" +
          "  color: #00838f;" +
          "  font-weight: 500;" +
          "  user-select: all;" +
          "  cursor: default;" +
          "  line-height: 1.4;" +
          "  vertical-align: baseline;" +
          "}" +
          ".tag-editor:empty:before {" +
          "  content: attr(data-placeholder);" +
          "  color: #999;" +
          "  pointer-events: none;" +
          "}"}
      </style>
      <div className="ql-toolbar ql-snow" role="toolbar" aria-label={t("automation.messageFormatting")}>
        <span className="ql-formats">
          <button type="button" className="ql-dynamicTag" title={t("automation.insertDynamicValue")}
            aria-label={t("automation.insertDynamicValue")} onMouseDown={event => event.preventDefault()}
            onClick={openTagPicker}><span>Tag</span><span className="tag-picker-caret" aria-hidden="true" /></button>
        </span>
      </div>
      <div
        ref={editorRef}
        className="tag-editor"
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={() => {
          if (onBlur)
            onBlur(editorRef.current ? getPlainText(editorRef.current) : value);
        }}
        suppressContentEditableWarning
        style={{
          minHeight: 80,
          padding: "8px 12px",
          border: "1px solid #cccccc",
          borderTop: "none",
          borderRadius: "0 0 4px 4px",
          fontSize: 14,
          fontFamily: "inherit",
          width: "100%",
          boxSizing: "border-box",
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          outline: "none",
          lineHeight: "1.6",
          ...style,
        }}
      />
      {showMenu && filteredTags.length > 0 && (
        <Box
          ref={menuRef}
          style={{
            position: "absolute",
            top: menuPos.top,
            left: menuPos.left,
            zIndex: 100,
          }}
          background="white"
          elevation="medium"
          round="xsmall"
          border={{ color: "light-4" }}
          width={{ min: "180px" }}
          overflow="auto"
          pad={{ vertical: "xxsmall" }}
        >
          {filteredTags.map((tag, i) => (
            <Box
              key={tag}
              pad={{ horizontal: "small", vertical: "xxsmall" }}
              background={i === selectedIndex ? "light-2" : "white"}
              hoverIndicator="light-2"
              onMouseDown={(e) => {
                e.preventDefault();
                insertTag(tag);
              }}
              style={{ cursor: "pointer" }}
            >
              <Text size="small">
                <span
                  style={{
                    display: "inline-block",
                    background: "#e0f7fa",
                    border: "1px solid #4dd0e1",
                    borderRadius: 12,
                    padding: "1px 8px",
                    color: "#00838f",
                    fontWeight: 500,
                    fontSize: 13,
                  }}
                >
                  {TAG_LABELS[tag] || tag}
                </span>
              </Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
